import React, { useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { Platform, TouchableOpacity } from 'react-native';
import Spacer from './Spacer';
import { B, P } from './Typography';
import Colors from '../constants/Colors';
import ListItem from './list/ListItem';
import { View } from 'react-native';
import { Feather, Ionicons } from '@expo/vector-icons';
import { differenceInDays, format, isSameDay } from 'date-fns';
import {
  OuraSpecificDataType,
  UserAttribute,
  UserAttributeInputType
} from '../models/UserAttribute';
import Button from './Button';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Link from './Link';
import { getHoursLabel } from './charts/LineChart';
import { SelectedWearableConfig } from '../screens/study/screen-components/plugins/WearableDataLineChartCardPlugin';
import { UserUnitSystem } from '../models/User';
import { useUserContext } from '../context/UserContext';

const StyledContainer = styled.View`
  width: 100%;
`;

const StyleMeasurementsContainer = styled.View`
  width: 100%;
  border-radius: 16px;
`;

const StyledListItem = styled(ListItem)<{ hasError?: boolean }>`
  height: 84px;
  margin-bottom: 12px;
  padding: 0 6px 0 12px;
  ${p =>
    p.hasError ? `border-color: #FFBACF; border-bottom-color: #FFBACF;` : ''}
`;

const StyledDetailContainer = styled.View`
  flex: 1;
  margin: 0 0 0 8px;
`;

const StyledValueText = styled(B)`
  color: ${Colors.secondaryText};
  margin: 0 6px 0 0;
  flex: 1;
  text-align: right;
`;

const StyledSquareImage = styled.Image`
  height: 40px;
  width: 40px;
`;

const StyledRowContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledValueContainer = styled.View`
  flex: 1;
  margin: 0 0 0 4px;
  flex-direction: row;
  align-items: center;
`;

const StyledMeasurementErrorContainer = styled.View`
  padding: 0 4px;
  flex-direction: row;
`;

const StyledEmptyView = styled.View`
  align-items: center;
  margin-bottom: ${Platform.OS === 'web' ? 48 : 16}px;
`;

const StyledEmptyImage = styled.Image`
  height: 203px;
  width: 254px;
`;

const StyledEmptyMessageText = styled(B)`
  text-align: center;
  width: 350px;
`;

const StyledMeasurementErrorText = styled.Text`
  color: #a10032;
  font-family: 'WorkSans-Medium';
  font-size: 16px;
  line-height: 19.5px;
  flex: 1;
  margin: 0 0 0 8px;
  align-self: center;
`;

const StyledEmptyButtonContainer = styled.View`
  flex: 1;
  width: 100%;
  max-width: 400px;
  margin: 70px 0 0 0;
`;

const StyledLink = styled(Link)`
  color: #a10032;
`;

const StyledSourceText = styled(P)`
  flex: 1;
  padding: 0 28px 0 12px;
  text-align: right;
`;

const StyledShowLink = styled(Link)`
  align-self: center;
`;

// convert metric to imperial
export const convertToImperial: (value: number, type?: string) => number = (
  value,
  type
) => {
  if (type) {
    let conversion: 'fahrenheit' | 'pound' | 'feet' | undefined;
    switch (type) {
      case '°F':
        conversion = 'fahrenheit';
        break;
      case 'lb':
        conversion = 'pound';
        break;
      case 'ft':
        conversion = 'feet';
        break;
    }
    return typeof value !== 'undefined'
      ? conversion === 'pound'
        ? value * 2.2046226218
        : conversion === 'fahrenheit'
        ? (value * 9) / 5 + 32
        : conversion === 'feet'
        ? value / 30.48
        : value
      : value;
  }
  return value;
};

export const measurements = (unitSystem?: UserUnitSystem) => [
  {
    name: 'Sleep',
    displayName: 'Sleep Duration',
    icon: require('../assets/icons/icon-sleep.png'),
    unit: 'hr'
  },
  {
    name: 'Blood Glucose',
    icon: require('../assets/icons/icon-blood-glucose.png'),
    unit: 'mmol/L'
  },
  {
    name: 'Heart Rate Variability',
    displayName: 'HRV',
    icon: require('../assets/icons/icon-hrv.png'),
    unit: 'ms'
  },
  {
    name: 'Heart Rate',
    icon: require('../assets/icons/icon-heart-rate.png'),
    unit: 'bpm'
  },
  {
    name: 'Resting Heart Rate',
    icon: require('../assets/icons/icon-resting-heart-rate.png'),
    unit: 'bpm'
  },
  {
    name: 'Oxygen Saturation',
    icon: require('../assets/icons/icon-oxygen-saturation.png'),
    unit: '%'
  },
  {
    name: 'Step Count',
    displayName: 'Steps',
    icon: require('../assets/icons/icon-steps.png'),
    unit: 'steps'
  },
  {
    name: 'Active Energy Burned',
    icon: require('../assets/icons/icon-active-energy-burned.png'),
    unit: 'cal'
  },
  {
    name: 'Basal Energy Burned',
    icon: require('../assets/icons/icon-basal-energy-burned.png'),
    unit: 'cal'
  },
  {
    name: 'Blood Alcohol Content',
    icon: require('../assets/icons/icon-measurement.png'),
    unit: '%'
  },
  {
    name: 'Blood Pressure',
    icon: require('../assets/icons/icon-blood-pressure.png'),
    unit: 'mmHg'
  },
  {
    name: 'Body Fat Percentage',
    icon: require('../assets/icons/icon-body-fat-percentage.png'),
    unit: '%'
  },
  {
    name: 'Body Mass Index',
    icon: require('../assets/icons/icon-bmi.png')
  },
  {
    name: 'Body Temperature',
    icon: require('../assets/icons/icon-body-temperature.png'),
    unit: unitSystem === UserUnitSystem.Imperial ? '°F' : '°C'
  },
  {
    name: 'Carbohydrates',
    icon: require('../assets/icons/icon-carbohydrates.png'),
    unit: 'g'
  },
  {
    name: 'Energy Consumed',
    icon: require('../assets/icons/icon-energy-consumed.png'),
    unit: 'cal'
  },
  {
    name: 'Height',
    icon: require('../assets/icons/icon-height.png'),
    unit: unitSystem === UserUnitSystem.Imperial ? 'ft' : 'cm'
  },
  {
    name: 'Lean Body Mass',
    icon: require('../assets/icons/icon-lean-body-mass.png'),
    unit: unitSystem === UserUnitSystem.Imperial ? 'lb' : 'kg'
  },
  {
    name: 'Protein Consumed',
    icon: require('../assets/icons/icon-protein-consumed.png'),
    unit: 'g'
  },
  {
    name: 'Total Fat Consumed',
    icon: require('../assets/icons/icon-total-fat-consumed.png'),
    unit: 'g'
  },
  {
    name: 'Weight',
    icon: require('../assets/icons/icon-weight.png'),
    unit: unitSystem === UserUnitSystem.Imperial ? 'lb' : 'kg'
  },
  {
    name: 'Calories Burned',
    icon: require('../assets/icons/icon-active-calories.png'),
    unit: 'cal'
  },
  // Additional data from Oura
  {
    name: 'Respiratory Rate',
    icon: require('../assets/icons/icon-average-breath.png'),
    unit: 'breaths/min'
  },
  {
    name: 'Lowest Heart Rate',
    icon: require('../assets/icons/icon-lowest-heart-rate.png'),
    unit: 'bpm'
  },
  {
    name: 'Sleep Efficiency',
    icon: require('../assets/icons/icon-sleep-efficiency.png'),
    unit: '%'
  },
  {
    name: 'Awake Time',
    icon: require('../assets/icons/icon-awake-time.png'),
    unit: 'min'
  },
  {
    name: 'Deep Sleep Duration',
    icon: require('../assets/icons/icon-deep-sleep.png'),
    unit: 'min'
  },
  {
    name: 'Sleep Latency',
    icon: require('../assets/icons/icon-sleep-latency.png'),
    unit: 'min'
  },
  {
    name: 'Light Sleep Duration',
    icon: require('../assets/icons/icon-light-sleep.png'),
    unit: 'min'
  },
  {
    name: 'REM Sleep Duration',
    icon: require('../assets/icons/icon-rem-sleep.png'),
    unit: 'min'
  },
  {
    name: 'Temperature Deviation',
    icon: require('../assets/icons/icon-temp-deviation.png'),
    unit: unitSystem === UserUnitSystem.Imperial ? '°F' : '°C'
  },
  {
    name: 'Temperature Trend Deviation',
    icon: require('../assets/icons/icon-temp-trend-deviation.png'),
    unit: unitSystem === UserUnitSystem.Imperial ? '°F' : '°C'
  },
  {
    name: 'Active Calories',
    icon: require('../assets/icons/icon-active-calories.png'),
    unit: 'cal'
  },
  {
    name: 'High Activity Time',
    icon: require('../assets/icons/icon-high-activity.png'),
    unit: 'min'
  },
  {
    name: 'Low Activity Time',
    icon: require('../assets/icons/icon-low-activity.png'),
    unit: 'min'
  },
  {
    name: 'Non-wear Time',
    icon: require('../assets/icons/icon-non-wear.png'),
    unit: 'min'
  },
  {
    name: 'Sedentary Time',
    icon: require('../assets/icons/icon-sedentary.png'),
    unit: 'min'
  },
  {
    name: 'SPO2 Nightly Disturbances',
    icon: require('../assets/icons/icon-disturbance-index.png')
  },
  {
    name: 'Workout',
    icon: require('../assets/icons/icon-workout.png'),
    unit: 'cal'
  }
];

function formatDateString(date: any, dateOnly?: boolean): string {
  const d = new Date(date);
  let dateStr = format(d, 'MM/dd/yy');
  const timeStr = format(d, 'h:mm aa');
  if (isSameDay(d, new Date())) {
    dateStr = 'Today';
  }
  return dateStr + (dateOnly ? '' : ' at ' + timeStr);
}

function customAttributeSort(a: UserAttribute, b: UserAttribute) {
  const priorities = ['Sleep', 'Heart Rate Variability', 'Steps'];
  const indexA = priorities.findIndex(name => a.type === name);
  const indexB = priorities.findIndex(name => b.type === name);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB; // Maintain priority order
  } else if (indexA !== -1) {
    return -1; // Place priority items first
  } else if (indexB !== -1) {
    return 1; // Place non-priority items after
  }
  return 0; // Keep existing order for others
}

type MyDataListProps = {
  wearableConfigs: SelectedWearableConfig[];
  latestAttributes: UserAttribute[];
  embedded?: boolean;
  onAddWearableClicked?: () => void;
};

const MyDataList: React.FC<MyDataListProps> = ({
  wearableConfigs,
  latestAttributes,
  embedded,
  onAddWearableClicked
}) => {
  const { user } = useUserContext();
  const [showAllAttributes, setShowAllAttributes] = useState<boolean>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const displayedAttributes = useMemo(() => {
    const filteredAttributes = latestAttributes.filter(
      (
        value: UserAttribute,
        index: number,
        userAttributes: UserAttribute[]
      ) => {
        // check if the user has already selected a source tab on the details screen
        if (wearableConfigs.length > 0) {
          // filter the specific config for each user attribute type
          const filteredWearableConfig = wearableConfigs.find(
            config => config.type === value.type
          );
          if (filteredWearableConfig) {
            return (
              userAttributes.findIndex(
                userAttribute =>
                  userAttribute.type === value.type &&
                  (filteredWearableConfig?.source === userAttribute.source ||
                    filteredWearableConfig?.sourceName ===
                      userAttribute.sourceName)
              ) === index
            );
          }
        }
        return (
          userAttributes.findIndex(
            userAttribute => userAttribute.type === value.type
          ) === index
        );
      }
    );
    if (wearableConfigs.length > 0) {
      // return default values if the selected source tab does not have user attribute data
      latestAttributes.forEach((value: UserAttribute) => {
        const filteredWearableConfig = wearableConfigs.find(
          config => config.type === value.type
        );
        const matchedWearable = latestAttributes.find(
          userAttribute =>
            filteredWearableConfig?.type === userAttribute.type &&
            (filteredWearableConfig?.source === userAttribute.source ||
              filteredWearableConfig?.sourceName === userAttribute.sourceName)
        );
        if (!matchedWearable && filteredWearableConfig) {
          filteredAttributes.push({
            created: new Date(),
            inputType: UserAttributeInputType.Wearable,
            numberValue: 0,
            source: filteredWearableConfig.source,
            sourceName: filteredWearableConfig.sourceName,
            subSource: filteredWearableConfig.subSource,
            type: filteredWearableConfig.type
          } as any);
        }
      });
    }
    return filteredAttributes
      .sort(customAttributeSort)
      .splice(0, showAllAttributes ? latestAttributes.length : 5);
  }, [latestAttributes, showAllAttributes, wearableConfigs]);

  return (
    <StyledContainer>
      <StyleMeasurementsContainer>
        {displayedAttributes &&
          displayedAttributes?.length > 0 &&
          displayedAttributes.map((item, index) => {
            const date = new Date();
            date.setHours(23, 59, 59, 59);
            const staleData = item.endDate
              ? Math.abs(differenceInDays(date, item.endDate)) >= 2
              : true;
            const measurement = measurements(user?.unitSystem).find(
              icon => icon.name === item.type || icon.displayName === item.type
            );
            const name = measurement?.displayName || measurement?.name || '';
            const roundedValue = ![
              OuraSpecificDataType.TemperatureDeviation.valueOf(),
              OuraSpecificDataType.TemperatureTrendDeviation.valueOf()
            ].includes(name);
            return (
              <View key={`${item.type}${item.endDate}-${index}`}>
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={() => {
                    navigation.navigate('MyDataMeasurementDetailsScreen', {
                      source: item.source,
                      type: item.type,
                      isEmbedded: embedded
                    });
                  }}
                >
                  <StyledListItem framed filled hasError={staleData}>
                    {item.type && (
                      <View>
                        <StyledSquareImage
                          resizeMode='contain'
                          source={measurement?.icon || ''}
                        />
                      </View>
                    )}
                    <StyledDetailContainer>
                      <StyledRowContainer>
                        <B color='dark'>{name}</B>
                        <StyledValueContainer>
                          <StyledValueText
                            numberOfLines={1}
                            ellipsizeMode='tail'
                            color='dark'
                          >
                            {measurement?.unit === 'hr'
                              ? getHoursLabel(item?.numberValue || 0)
                              : measurement?.unit === 'mmHg'
                              ? `${item?.textValue} ${measurement.unit}`
                              : (user?.unitSystem === UserUnitSystem.Imperial
                                  ? convertToImperial(
                                      item?.numberValue || 0,
                                      measurement?.unit
                                    )
                                  : item?.numberValue || 0
                                ).toLocaleString(undefined, {
                                  maximumFractionDigits: roundedValue ? 0 : 2,
                                  minimumFractionDigits: 0
                                }) +
                                ' ' +
                                (measurement?.unit || '')}
                          </StyledValueText>
                          <Feather
                            name='chevron-right'
                            size={22}
                            color='black'
                          />
                        </StyledValueContainer>
                      </StyledRowContainer>
                      <StyledRowContainer>
                        <P color='#858CA2'>
                          {item.created &&
                            formatDateString(
                              item.created,
                              item.inputType !== UserAttributeInputType.Wearable
                            )}
                        </P>
                        <StyledSourceText
                          numberOfLines={1}
                          ellipsizeMode='tail'
                          color='#858CA2'
                        >
                          {item.sourceName || item.source}
                        </StyledSourceText>
                      </StyledRowContainer>
                    </StyledDetailContainer>
                  </StyledListItem>
                </TouchableOpacity>
                {staleData && (
                  <>
                    {Platform.OS === 'web' && <Spacer size='small' />}
                    <StyledMeasurementErrorContainer>
                      <Ionicons
                        name='warning-outline'
                        size={24}
                        color='#A10032'
                      />
                      <StyledMeasurementErrorText>
                        Data may be missing or outdated. Please{' '}
                        <StyledLink
                          disabled={embedded}
                          onPress={onAddWearableClicked}
                        >
                          re-sync
                        </StyledLink>{' '}
                        your wearable.
                      </StyledMeasurementErrorText>
                    </StyledMeasurementErrorContainer>
                    <Spacer />
                  </>
                )}
              </View>
            );
          })}
        {latestAttributes && latestAttributes.length > 5 && (
          <StyledShowLink
            inverse
            onPress={() => setShowAllAttributes(!showAllAttributes)}
          >
            {showAllAttributes ? 'Show Less' : 'Show More'}
          </StyledShowLink>
        )}
        {displayedAttributes && displayedAttributes.length <= 0 && (
          <StyledEmptyView>
            <Spacer size={8} />
            <StyledEmptyImage
              source={require('../assets/my-data/measurements-empty.png')}
            />
            <Spacer size={5} />
            <StyledEmptyMessageText color='dark'>
              There are no measurements available at this time.
            </StyledEmptyMessageText>
            <StyledEmptyButtonContainer>
              <Button
                disabled={embedded}
                fullWidth
                onPress={onAddWearableClicked}
              >
                Add Wearables
              </Button>
            </StyledEmptyButtonContainer>
          </StyledEmptyView>
        )}
      </StyleMeasurementsContainer>
    </StyledContainer>
  );
};

export default MyDataList;
